import React, { useState } from 'react';
import { useTable, useSortBy, usePagination } from 'react-table';
import { APIUser, User } from '../../../types/user';
import { Card, Table, Input, Button } from 'reactstrap';
import styles from '../../../styles/archiveui.module.css';
import SearchBar from './SearchBar';
import { AiFillEdit } from 'react-icons/ai';
import { useHistory } from 'react-router-dom';

interface UserTableProps {
  users: APIUser[];
  accountId: string;
}

const APIUserTable = ({ users, accountId }: UserTableProps) => {
  const history = useHistory();
  const [searchText, updateSearchText] = useState<string>('');
  const [statusFilter, updateStatusFilter] = useState<string>('ACTIVE');
  const data = users;

  const columns: any = React.useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'displayName', // accessor is the "key" in the data
      },
      {
        Header: 'Client ID',
        accessor: 'appClientId',
      },
      {
        Header: 'Actions',
        // eslint-disable-next-line react/display-name
        Cell: () => (
          <td className={styles.actionsCell}>
            <AiFillEdit id="edit" color="#212529" />
          </td>
        ),
      },
    ],
    [],
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable({ columns, data, initialState: { pageIndex: 0, pageSize: 10 } }, useSortBy, usePagination);

  const onRowClick = (cell: User | any) => {
    if (cell.column.Header === 'Actions') {
      history.push(`/apiuserdetail/${accountId}/${cell.row.original.id}`);
    }
  };

  const table = document.getElementsByClassName('table-responsive')[0] as HTMLElement;
  if (table != undefined) {
    table.style.borderRadius = '.5em';
  }

  return (
    <div>
      <div className={styles.userTableContainer}>
        <SearchBar
          accountId={accountId}
          searchText={searchText}
          updateSearchText={updateSearchText}
          statusFilter={statusFilter}
          updateStatusFilter={updateStatusFilter}
        />
        <Card className={styles.tableContainer}>
          <Table striped hover size="sm" id="auditTable" responsive style={{ backgroundColor: 'white' }} {...getTableProps()}>
            <thead className={styles.tableHeader}>
              {headerGroups.map((headerGroup) => (
                // eslint-disable-next-line react/jsx-key
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => {
                    if (column.Header === 'Last Name') {
                      return (
                        // eslint-disable-next-line react/jsx-key
                        <th {...column.getHeaderProps(column.getSortByToggleProps())} className={styles.IdHeader}>
                          {column.render('Header')}
                          {/* Add a sort direction indicator */}
                          <span>{column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}</span>
                        </th>
                      );
                    } else if (column.Header === 'Actions') {
                      return (
                        // eslint-disable-next-line react/jsx-key
                        <th {...column.getHeaderProps(column.getSortByToggleProps())} className={styles.actionsHeader}>
                          {column.render('Header')}
                          {/* Add a sort direction indicator */}
                          <span>{column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}</span>
                        </th>
                      );
                    } else {
                      return (
                        // eslint-disable-next-line react/jsx-key
                        <th {...column.getHeaderProps(column.getSortByToggleProps())} className={styles.th}>
                          {column.render('Header')}
                          {/* Add a sort direction indicator */}
                          <span>{column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}</span>
                        </th>
                      );
                    }
                  })}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page
                .filter((row) => {
                  if (statusFilter !== '*' && statusFilter !== row.original.status) {
                    return;
                  }
                  const normalizedSearch = searchText.trim().toLowerCase();
                  if (normalizedSearch === '') {
                    return row;
                  } else if (row.original.displayName?.toLowerCase().includes(normalizedSearch)) {
                    return row;
                  } else if ((row.original.appClientId ?? '').toLowerCase().includes(normalizedSearch)) {
                    return row;
                  } else if ((row.original.id ?? '').toLowerCase().includes(normalizedSearch)) {
                    return row;
                  } else if ((row.original.clientSecret ?? '').toLowerCase().includes(normalizedSearch)) {
                    return row;
                  }
                })
                .map((row, i) => {
                  prepareRow(row);
                  return (
                    // eslint-disable-next-line react/jsx-key
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => {
                        if (cell.column.Header === 'Actions') {
                          return (
                            // eslint-disable-next-line react/jsx-key
                            <td
                              style={{ display: 'flex', flexDirection: 'column', justifyContent: 'end' }}
                              onClick={() => {
                                onRowClick(cell);
                              }}
                              {...cell.getCellProps()}
                            >
                              {cell.render('Cell')}
                            </td>
                          );
                        } else {
                          return (
                            // eslint-disable-next-line react/jsx-key
                            <td
                              onClick={() => {
                                onRowClick(cell);
                              }}
                              {...cell.getCellProps()}
                            >
                              {cell.render('Cell')}
                            </td>
                          );
                        }
                      })}
                    </tr>
                  );
                })}
            </tbody>
          </Table>
        </Card>
      </div>
      <div className="pagination" style={{ justifyContent: 'end', marginTop: '.5em' }}>
        <div style={{ marginRight: '.5em' }}>
          <Button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
            {'<<'}
          </Button>{' '}
          <Button onClick={() => previousPage()} disabled={!canPreviousPage}>
            {'<'}
          </Button>{' '}
          <Button onClick={() => nextPage()} disabled={!canNextPage}>
            {'>'}
          </Button>{' '}
          <Button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
            {'>>'}
          </Button>{' '}
        </div>
        <div style={{ marginRight: '.5em' }}>
          <span>
            Page{' '}
            <strong>
              {pageIndex + 1} of {pageOptions.length}
            </strong>{' '}
          </span>
        </div>
        <Input
          style={{ maxWidth: '10em' }}
          type="select"
          aria-label="Select Page Size"
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
          }}
        >
          {[10, 20, 30, 40, 50].map((pageSize) => (
            <option key={pageSize} value={pageSize}>
              Show {pageSize}
            </option>
          ))}
        </Input>
      </div>
    </div>
  );
};

export default APIUserTable;
